import { useState } from "react";
import { formatPairLabelSafe } from "../data/pairs";

/* ---------------------------------------------------------------
   SCANNER BOT — POST /bot/scanner/ { photo: "<data:...base64>" } ->
   a signal. Body is plain JSON (base64-encoded photo), matching how
   every other endpoint in this backend reads request.data — see the
   comment in uploadScan() below for why a multipart upload doesn't
   work here. The response has the exact same shape as POST
  /bot/signal/ (direction, entry_price, expiration, time_seconds,
  entry_section, comment), which is
   why this hook and its overlay reuse ActiveSignalOverlay wholesale
   instead of duplicating it: the photo just replaces "pick a pair by
   hand" as the way the traded asset is chosen, everything downstream
   (full-screen countdown derived from `timeframe` that can't be
   dismissed, then a result to record once it expires) is identical.

   Persistence mirrors useActiveSignal.js: the in-flight scan is
   written to localStorage the moment a response comes back, and only
   cleared once a result is submitted, so it survives a layout
   remount or a backgrounded/reloaded Telegram WebView the same way.
------------------------------------------------------------------ */

const ACTIVE_SCAN_STORAGE_KEY = "pj_active_scan";

function loadStoredScan() {
  try {
    const raw = localStorage.getItem(ACTIVE_SCAN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.signal || !parsed?.startedAt) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function storeScan(entry) {
  try {
    localStorage.setItem(ACTIVE_SCAN_STORAGE_KEY, JSON.stringify(entry));
  } catch (e) {}
}

function clearStoredScan() {
  try {
    localStorage.removeItem(ACTIVE_SCAN_STORAGE_KEY);
  } catch (e) {}
}

/* FileReader -> "data:image/png;base64,...." (kept as a data URL rather
   than stripped-down raw base64 so the mime type travels with it — if
   the backend field turns out to want raw base64 without the prefix,
   just `.split(",")[1]` the result before sending). */
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("file read failed"));
    reader.readAsDataURL(file);
  });
}

/* Owns the active-scan lifecycle for the whole app (used once from
   App(), same as useActiveSignal) so it survives a wide/narrow layout
   remount without losing the countdown. */
export function useActiveScan(authFetch) {
  const [entry, setEntry] = useState(() => loadStoredScan());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [resultSubmitting, setResultSubmitting] = useState(false);
  const [resultError, setResultError] = useState(null);

  const uploadScan = async (file, timeframe) => {
    if (submitting || !file) return;
    setSubmitting(true);
    setError(null);
    try {
      // Every other endpoint in this backend (request_signal above is
      // the clearest example) reads the body via DRF's plain
      // request.data — i.e. JSON, not multipart. Sending the photo as
      // multipart/form-data made DRF's JSONParser choke trying to
      // utf-8-decode raw PNG bytes ("\x89PNG..."), hence the
      // "can't decode byte 0x89" error. So: base64-encode the photo
      // and send it as an ordinary JSON field instead, same shape as
      // { asset, deal_type } on the Signals bot. `timeframe` rides
      // along the same request the same way `deal_type` does there.
      const photoDataUrl = await readFileAsDataUrl(file);
      const res = await authFetch("bot/scanner/", {
        method: "POST",
        body: JSON.stringify({ photo: photoDataUrl, timeframe }),
      });
      if (!res.ok) {
        // Trial accounts get one flat scanner bucket (5 scans) — the
        // backend flags exhaustion distinctly (trial_limit_reached:
        // true, 403) instead of just failing generically.
        let body = null;
        try {
          body = await res.json();
        } catch (e) {}
        if (res.status === 403 && body?.trial_limit_reached === true) {
          throw new Error("trial_limit");
        }
        throw new Error(`scanner API ${res.status}`);
      }
      const data = await res.json();
      const newEntry = { signal: data, startedAt: Date.now() };
      setEntry(newEntry);
      storeScan(newEntry);
    } catch (e) {
      setError(e.message || "error");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelScan = () => {
    setEntry(null);
    setError(null);
    setResultError(null);
    clearStoredScan();
  };

  const submitResult = async (profit) => {
    if (resultSubmitting || !entry) return;
    setResultSubmitting(true);
    setResultError(null);
    try {
      // Whatever asset label the backend recognised from the photo
      // (entry.signal.asset) is what gets recorded against, same as
      // the Signals bot records the pair the user picked by hand.
      const asset = entry.signal.asset || entry.signal.pair || "SCANNER";
      const res = await authFetch("bot/stats/", {
        method: "POST",
        body: JSON.stringify({ asset, source: "SCANNER", profit }),
      });
      if (!res.ok) throw new Error(`stats API ${res.status}`);
      setEntry(null);
      clearStoredScan();
    } catch (e) {
      setResultError(e.message || "error");
    } finally {
      setResultSubmitting(false);
    }
  };

  // What the overlay heading shows — falls back gracefully if the
  // backend doesn't return a recognisable asset code for this scan.
  const titleLabel = entry?.signal?.asset ? formatPairLabelSafe(entry.signal.asset) : null;

  return {
    entry,
    titleLabel,
    submitting,
    error,
    uploadScan,
    cancelScan,
    resultSubmitting,
    resultError,
    submitResult,
  };
}
